<script lang="ts" setup>
import { propTypes } from '@/utils/propTypes'
import { isNumber } from '@/utils/is'

defineOptions({ name: 'Dialog' })

const slots = useSlots()
const emits = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const props = defineProps({
  modelValue: propTypes.bool.def(false),
  title: propTypes.string.def('Dialog'),
  fullscreen: propTypes.bool.def(true),
  width: propTypes.oneOfType([String, Number]).def('40%'),
  scroll: propTypes.bool.def(false), // 是否开启滚动条。如果是的话，按照 maxHeight 设置固定高度（内容少时会留白）
  bodyScroll: propTypes.bool.def(true), // 未开启 scroll 时，内容超出最大高度自动出现滚动条；内容较少时高度自适应
  maxHeight: propTypes.oneOfType([String, Number]).def('400px'),
  loading: propTypes.bool.def(false)
})

const getBindValue = computed(() => {
  const delArr: string[] = [
    'fullscreen',
    'title',
    'maxHeight',
    'appendToBody',
    'loading',
    'bodyScroll'
  ]
  const attrs = useAttrs()
  const obj = { ...attrs, ...props }
  for (const key in obj) {
    if (delArr.indexOf(key) !== -1) {
      delete obj[key]
    }
  }
  return obj
})

const isFullscreen = ref(false)

const toggleFull = () => {
  isFullscreen.value = !unref(isFullscreen)
}

const dialogHeight = ref(isNumber(props.maxHeight) ? `${props.maxHeight}px` : props.maxHeight)

watch(
  () => isFullscreen.value,
  async (val: boolean) => {
    await nextTick()
    if (val) {
      const windowHeight = document.documentElement.offsetHeight
      dialogHeight.value = `${windowHeight - 55 - 60 - (slots.footer ? 63 : 0)}px`
    } else {
      dialogHeight.value = isNumber(props.maxHeight) ? `${props.maxHeight}px` : props.maxHeight
    }
  },
  {
    immediate: true
  }
)

const dialogStyle = computed(() => {
  return {
    height: unref(dialogHeight)
  }
})

const closing = ref(false)

// 取消按钮：单独抽成函数，避免在模板里写多语句行内表达式
// （prettier 会把 `a(); b()` 拆成多行并丢掉分号，导致 Vue 模板编译报错）
function cancelHandler() {
  emits('update:modelValue', false)
  emits('cancel')
}

function closeHandler() {
  emits('update:modelValue', false)
  closing.value = true
}

function closedHandler() {
  closing.value = false
}
</script>

<template>
  <ElDialog
    v-bind="getBindValue"
    :close-on-click-modal="true"
    :fullscreen="isFullscreen"
    :width="width"
    destroy-on-close
    lock-scroll
    draggable
    class="com-dialog"
    :show-close="false"
    @close="closeHandler"
    @closed="closedHandler"
  >
    <template #header="{ close }">
      <div class="relative h-54px flex items-center justify-between pl-15px pr-15px">
        <slot name="title">
          {{ title }}
        </slot>
        <div
          class="absolute right-15px top-[50%] h-54px flex translate-y-[-50%] items-center justify-between"
        >
          <Icon
            v-if="fullscreen"
            class="is-hover mr-10px cursor-pointer"
            :icon="isFullscreen ? 'radix-icons:exit-full-screen' : 'radix-icons:enter-full-screen'"
            color="var(--el-color-info)"
            hover-color="var(--el-color-primary)"
            @click="toggleFull"
          />
          <Icon
            class="is-hover cursor-pointer"
            icon="ep:close"
            hover-color="var(--el-color-primary)"
            color="var(--el-color-info)"
            @click.stop="close"
          />
        </div>
      </div>
    </template>

    <div v-loading="loading" :style="loading ? { minHeight: '120px' } : undefined">
      <template v-if="!loading">
        <ElScrollbar v-if="scroll" :style="dialogStyle">
          <slot></slot>
        </ElScrollbar>
        <!-- 未开启 scroll：内容超出最大高度时在弹窗内滚动，内容较少时高度自适应（不留白） -->
        <ElScrollbar v-else-if="bodyScroll" class="com-dialog__body-scroll">
          <slot></slot>
        </ElScrollbar>
        <slot v-else></slot>
      </template>
    </div>
    <template #footer>
      <div :style="{ 'pointer-events': closing ? 'none' : 'auto' }">
        <template v-if="slots.footer">
          <slot name="footer"></slot>
        </template>
        <template v-else>
          <el-button @click="cancelHandler">取消</el-button>
          <el-button type="primary" @click="emits('confirm')">确定</el-button>
        </template>
      </div>
    </template>
  </ElDialog>
</template>

<style lang="scss">
.com-dialog {
  .#{$elNamespace}-overlay-dialog {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .#{$elNamespace}-dialog {
    margin: 0 !important;

    &__header {
      height: 54px;
      padding: 0;
      margin-right: 0 !important;
      background-color: #fafbfc;
      border-radius: 8px 8px 0 0;
    }

    &__body {
      padding: 15px !important;
      background-color: #fff;
    }

    &__footer {
      background-color: #fafbfc;
      text-align: right;
    }

    &__headerbtn {
      top: 0;
    }
  }
}

/**
 * 弹窗内容区自动滚动（Dialog 的 bodyScroll 属性，默认开启）
 *
 * el-scrollbar 默认 .el-scrollbar{height:100%} + .el-scrollbar__wrap{height:100%}，
 * 只给根元素加 max-height 会导致 wrap 高度仍为内容高度、被根元素 overflow:hidden 裁掉且无法滚动。
 * 因此必须让 __wrap 同步继承 max-height 才能滚起来。
 * 用 max-height（而非固定 height）才能做到：内容少时高度自适应不留白，内容多时才出现滚动条。
 *
 * 240px = 弹窗头部 54 + 内容区上下 padding 30 + 底部按钮区 63 + 弹窗与视口上下边缘留白约 93
 */
.com-dialog__body-scroll {
  max-height: calc(100vh - 240px);

  .#{$elNamespace}-scrollbar__wrap {
    max-height: inherit;
  }
}

// 全屏时弹窗占满视口，放宽最大高度（仅需扣除头部与底部按钮区）
.#{$elNamespace}-dialog.is-fullscreen .com-dialog__body-scroll {
  max-height: calc(100vh - 160px);
}
</style>
